import { action, comparer, computed, observable, reaction, when } from "mobx";
import { atom } from "../atom";
import { DEFAULT_CANVAS_OFFSET, MEDIUM_WIDTH_BREAKPOINT } from "../Constants";
import {
  Action,
  ActivePaneState,
  Canvas,
  CanvasLayout,
  InputMode,
  PaneInputGlobalKey,
  PaneType,
  Position,
  SandboxLayoutOverridePane,
} from "../Types";
import { throttle } from "../Utils";
import { MAGIC_LAYOUT_ID } from "./SandboxConstants";

export const PLACEHOLDER_CANVAS_ID = "";
export const canvasAtom = atom<Canvas>({
  id: PLACEHOLDER_CANVAS_ID,
  name: "",
  panes: [],
  layouts: [],
  createdTime: Date.now(),
});

// this represents the layout id in the browser url
export const urlLayoutIdAtom = atom<string | undefined>(undefined);
// canvasLayoutIdAtom.value != urlLayoutIdAtom.value when magic layout is loading
export const canvasLayoutIdAtom = atom<string | undefined>(undefined);
export const magicLayoutAtom = atom<CanvasLayout | undefined>(undefined);
export const layoutComputed = computed(() => {
  const layoutId = canvasLayoutIdAtom.value;
  if (layoutId === undefined) {
    return undefined;
  }
  if (layoutId === MAGIC_LAYOUT_ID) {
    return magicLayoutAtom.value;
  }
  return canvasAtom.value.layouts.find((l) => l.id === layoutId);
});
export const layoutsMetadataComputed = computed(
  () => canvasAtom.value.layouts.map((l) => ({ id: l.id, name: l.name })),
  { equals: comparer.structural }
);
export const isEditingLayoutAtom = atom(false);
export const isOnCustomLayoutComputed = computed(
  () =>
    canvasLayoutIdAtom.value !== undefined &&
    canvasLayoutIdAtom.value !== MAGIC_LAYOUT_ID
);
reaction(
  () => isOnCustomLayoutComputed.get(),
  action((isOnCustomLayout) => {
    if (!isOnCustomLayout) {
      isEditingLayoutAtom.value = false;
    }
  })
);
export const isViewingLayoutComputed = computed(
  () => layoutComputed.get() !== undefined && !isEditingLayoutAtom.value
);
export const isViewingCustomLayoutComputed = computed(
  () =>
    isViewingLayoutComputed.get() &&
    canvasLayoutIdAtom.value !== MAGIC_LAYOUT_ID
);
export const shouldClonePaneInputsBox = observable.box(false);

export const activePaneStates = observable.map<string, ActivePaneState>(
  {},
  { deep: false }
);
export const layoutPreviewAddPaneIdBox = observable.box<string | undefined>(
  undefined
);

export const sandboxLayoutOverrideBox = observable.box<
  { [paneId: string]: SandboxLayoutOverridePane } | undefined
>(undefined);

export const undoDeletePaneStackAtom = atom<Action[]>([]);
export const shouldShowCanvasWiresComputed = computed(
  () => !isOnCustomLayoutComputed.get()
);
export const canCreateNewPaneComputed = computed(
  () => canvasLayoutIdAtom.value === undefined
);
export const isSafeModeAtom = atom(false);
export const initialSearchParamEntriesBox = observable.box<
  Record<string, string>
>({});
export const usedDefaultLayoutAtom = atom(false);
when(
  () => usedDefaultLayoutAtom.value,
  () => {
    when(
      () => canvasLayoutIdAtom.value === undefined,
      action(() => {
        usedDefaultLayoutAtom.value = false;
      })
    );
  }
);
export const windowViewportAtom = atom<Position>([
  window.innerWidth,
  window.innerHeight,
]);
window.addEventListener(
  "resize",
  throttle(
    action(() => {
      windowViewportAtom.value = [window.innerWidth, window.innerHeight];
    }),
    50
  )
);

export const headerHeightComputed = computed(() =>
  windowViewportAtom.value[0] >= MEDIUM_WIDTH_BREAKPOINT ? 48 : 80
);
export const canvasViewport = observable<{
  offset: Position;
  zoom: number;
}>({
  offset: [...DEFAULT_CANVAS_OFFSET],
  zoom: 1,
});
reaction(
  () => canvasLayoutIdAtom.value,
  action(() => {
    canvasViewport.offset = [...DEFAULT_CANVAS_OFFSET];
  })
);
function computeCanvasTransform(): Position {
  return [
    -canvasViewport.offset[0] * canvasViewport.zoom,
    -canvasViewport.offset[1] * canvasViewport.zoom +
      headerHeightComputed.get(),
  ];
}
export const canvasTransformComputed = computed(() => computeCanvasTransform());
export const canvasViewportShouldAnimateAtom = atom<boolean | number>(false);
export const exposeZoomPrevViewportBox = observable.box<
  { offset: Position; zoom: number } | undefined
>(undefined);

export const focusedPaneIdAtom = atom<string | undefined>(undefined);
export const selectedPaneIdsAtom = atom<string[]>([]);
export const sidebarHoverPaneIdAtom = atom<string | undefined>(undefined);
export const functionPaneHighlightIdBox = observable.box<string | undefined>(
  undefined
);
reaction(
  () => isEditingLayoutAtom.value,
  action((isEditingLayout) => {
    if (!isEditingLayout) {
      sidebarHoverPaneIdAtom.value = undefined;
    }
  })
);
export const showPaneSettingsPaneIdAtom = atom<string | undefined>(undefined);

export type CanvasTool =
  | ["pointer"]
  | ["pan", ["pointer"] | ["pane", PaneType] | undefined]
  | ["pane", PaneType];
export const selectedToolAtom = atom<CanvasTool>(["pointer"]);

export const globalValuesMobx = observable<Record<string, any>>({}, undefined, {
  deep: false,
});

export const paneKeyboardMappingAtom = atom<
  { [paneId: string]: string } | undefined
>(undefined);
export const keyboardTransformPaneIdBox = observable.box<string | undefined>(
  undefined
);
export const inputModeComputed = computed(() => {
  if (exposeZoomPrevViewportBox.get() !== undefined) {
    return InputMode.ExposeZoom;
  }
  if (selectedToolAtom.value[0] === "pan") {
    return InputMode.Panning;
  }
  if (keyboardTransformPaneIdBox.get() !== undefined) {
    return InputMode.TransformPane;
  }
  if (paneKeyboardMappingAtom.value !== undefined) {
    return InputMode.PickFocusPane;
  }
  return InputMode.Normal;
});

type TransferPaneInputSource = {
  paneInputs: PaneInputGlobalKey[];
  position: Position;
  originalPaneOutput: [paneId: string, outputIndex: number];
  hoverPaneOutput: [paneId: string, outputIndex: number] | undefined;
};
export const transferPaneInputSourceBox = observable.box<
  TransferPaneInputSource | undefined
>(undefined);

// This is a hack that allows us to show a popover that doesn't steal focus
// but we still want to trigger keyboard shortcuts
export const isShowingNonFocusedPopoverBox = observable.box(false);
